import { memo, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { DropdownChevron } from "@/components/common/Disclosure";
import { Icon } from "@/components/common/Icon";
import { ContextMenu, type ContextMenuEntry } from "@/components/common/ContextMenu";
import { listChatModels, type ChatModelOption } from "@/lib/api";
import { SUBAGENT_PROFILES, subagentProfileLabel } from "@/lib/chat";
import { ChatEffortSelector } from "@/components/chat/ChatEffortSelector";
import { dedupeChatModels, resolveEffortForModel } from "@/lib/chat-models";
import { DEFAULT_CHAT_EFFORT, DEFAULT_CHAT_MODEL, useChatStore } from "@/stores/useChatStore";

/** ラベルと右寄せの現在値 (+サブメニューを示すシェブロン) を並べたメニュー行の表示内容。 */
function menuRowContent(label: string, value?: string, showChevron = false) {
	return (
		<span style={{ display: "flex", alignItems: "center", width: "100%" }}>
			<span>{label}</span>
			<span style={{ display: "flex", alignItems: "center", gap: 8, marginLeft: "auto" }}>
				{value !== undefined && <span style={{ color: "var(--fg-3)" }}>{value}</span>}
				{showChevron && <Icon name="chevronR" size={12} color="var(--fg-3)" />}
			</span>
		</span>
	);
}

interface ChatModelMenuButtonProps {
	models: ChatModelOption[];
	value: string;
	onChange: (model: ChatModelOption) => void;
	disabled: boolean;
	additionalItems?: ContextMenuEntry[];
	tooltip?: string;
	testId?: string;
	menuTestId?: string;
	itemTestIdPrefix?: string;
	appearance?: "plain" | "secondary";
}

/** モデル一覧と選択値だけを受け取る、store 非依存のモデルメニューボタン。 */
export function ChatModelMenuButton({
	models,
	value,
	onChange,
	disabled,
	additionalItems = [],
	tooltip,
	testId = "chat-model-selector",
	menuTestId = "chat-model-selector-menu",
	itemTestIdPrefix = "chat-model-selector-item",
	appearance = "plain",
}: ChatModelMenuButtonProps) {
	const [menuOpen, setMenuOpen] = useState(false);
	const triggerRef = useRef<HTMLButtonElement>(null);
	const selected = models.find((model) => model.id === value);
	const modelItem = (model: ChatModelOption): ContextMenuEntry => ({
		key: model.id,
		label: model.name,
		selectable: true,
		selected: model.id === value,
		testId: `${itemTestIdPrefix}-${model.id}`,
		onSelect: () => onChange(model),
	});
	const otherModels = models.filter((model) => model.listedUnderOthers);
	const menuItems: ContextMenuEntry[] = [
		...models.filter((model) => !model.listedUnderOthers).map(modelItem),
		...(otherModels.length === 0
			? []
			: [
					{
						key: "other-models",
						label: "そのほか",
						content: menuRowContent("そのほか", undefined, true),
						closeOnSelect: false,
						showSelectionCheck: false,
						subMenu: otherModels.map(modelItem),
						subMenuMinWidth: 180,
						testId: `${itemTestIdPrefix}-others`,
					},
				]),
		...(additionalItems.length === 0
			? []
			: ([{ separator: true }, ...additionalItems] satisfies ContextMenuEntry[])),
	];

	return (
		<>
			<button
				ref={triggerRef}
				type="button"
				disabled={disabled}
				onClick={() => setMenuOpen((open) => !open)}
				data-testid={testId}
				data-tooltip={tooltip}
				className="izanagi-dropdown-trigger"
				data-size="compact"
				data-appearance={appearance}
			>
				<span data-testid={`${testId}-label`}>{selected ? selected.name : "読み込み中…"}</span>
				{/* 入力欄の外に単独で置くときは、メニューが開くことをシェブロンで示す。 */}
				{appearance === "secondary" && (
					<DropdownChevron open={menuOpen} size={12} color="var(--fg-3)" />
				)}
			</button>
			{/* チャット履歴のブロック (paint containment) 内でも fixed 配置が効くよう body へ出す。 */}
			{menuOpen &&
				triggerRef.current &&
				createPortal(
					<ContextMenu
						position={{ anchor: triggerRef.current, align: "right" }}
						onClose={() => setMenuOpen(false)}
						testId={menuTestId}
						checkAlign="trailing"
						minWidth={180}
						items={menuItems}
					/>,
					document.body,
				)}
		</>
	);
}

/** 次回のチャット送信に適用するモデルと推論レベルを選択する UI。 */
export const ChatModelSelector = memo(function ChatModelSelector() {
	const selectedModel = useChatStore((s) => s.selectedModel);
	const selectedEffort = useChatStore((s) => s.selectedEffort);
	const subagentModels = useChatStore((s) => s.subagentModels);
	const setSelectedModel = useChatStore((s) => s.setSelectedModel);
	const setSelectedEffort = useChatStore((s) => s.setSelectedEffort);
	const setSubagentModel = useChatStore((s) => s.setSubagentModel);
	const status = useChatStore((s) => s.status);
	const isLoadingChat = useChatStore((s) => s.isLoadingChat);
	const chatArchived = useChatStore((s) => s.chatArchived);
	const [models, setModels] = useState<ChatModelOption[]>([]);
	const [loadError, setLoadError] = useState(false);

	useEffect(() => {
		let cancelled = false;
		listChatModels()
			.then((availableModels) => {
				if (cancelled) return;
				setModels(dedupeChatModels(availableModels));
				setLoadError(false);
			})
			.catch((error: unknown) => {
				if (cancelled) return;
				console.error("モデル一覧の取得に失敗しました", error);
				setLoadError(true);
			});

		return () => {
			cancelled = true;
		};
	}, []);

	useEffect(() => {
		if (models.length === 0) return;
		const currentModel = models.find((candidate) => candidate.id === selectedModel);
		const model =
			currentModel ??
			models.find((candidate) => candidate.isDefault) ??
			models.find((candidate) => candidate.id === DEFAULT_CHAT_MODEL) ??
			models[0];
		if (model.id !== selectedModel) {
			setSelectedModel(model.id);
		}
		const hasBackendDefault = currentModel === undefined && model.defaultEffort !== undefined;
		const defaultEffort = hasBackendDefault
			? (model.defaultEffort ?? DEFAULT_CHAT_EFFORT)
			: DEFAULT_CHAT_EFFORT;
		const nextEffort = resolveEffortForModel(
			model,
			hasBackendDefault ? null : selectedEffort,
			defaultEffort,
		);
		if (nextEffort !== selectedEffort) {
			setSelectedEffort(nextEffort);
		}
	}, [models, selectedEffort, selectedModel, setSelectedEffort, setSelectedModel]);

	useEffect(() => {
		if (models.length === 0) return;
		for (const profile of SUBAGENT_PROFILES) {
			const modelId = subagentModels[profile];
			if (modelId !== undefined && !models.some((model) => model.id === modelId)) {
				setSubagentModel(profile, null);
			}
		}
	}, [models, setSubagentModel, subagentModels]);

	const disabled = models.length === 0 || status !== "idle" || isLoadingChat || chatArchived;
	const selected = models.find((model) => model.id === selectedModel);
	const supportedEfforts = selected?.supportedEfforts ?? [];
	const displayedEffort =
		selectedEffort !== null && supportedEfforts.includes(selectedEffort) ? selectedEffort : null;

	const selectModel = (model: ChatModelOption) => {
		setSelectedModel(model.id);
		setSelectedEffort(resolveEffortForModel(model, selectedEffort, DEFAULT_CHAT_EFFORT));
	};

	const settingsItems: ContextMenuEntry[] = [
		{
			key: "select-subagent-models",
			label: "サブエージェント",
			content: menuRowContent("サブエージェント", undefined, true),
			closeOnSelect: false,
			showSelectionCheck: false,
			subMenu: SUBAGENT_PROFILES.map((profile): ContextMenuEntry => {
				const selectedSubagentModel = subagentModels[profile];
				const display = models.find((model) => model.id === selectedSubagentModel);
				return {
					key: profile,
					label: subagentProfileLabel(profile),
					content: menuRowContent(
						subagentProfileLabel(profile),
						display ? display.name : "親と同じ",
					),
					closeOnSelect: false,
					showSelectionCheck: false,
					subMenuToggleIcon: true,
					subMenuOpenOnClick: true,
					subMenu: [
						{
							key: "inherit",
							label: "親と同じ",
							selectable: true,
							closeOnSelect: false,
							selected: selectedSubagentModel === undefined,
							testId: `chat-subagent-${profile}-item-inherit`,
							onSelect: () => setSubagentModel(profile, null),
						},
						...models.map((model) => ({
							key: model.id,
							label: model.name,
							selectable: true,
							closeOnSelect: false,
							selected: model.id === selectedSubagentModel,
							testId: `chat-subagent-${profile}-item-${model.id}`,
							onSelect: () => setSubagentModel(profile, model.id),
						})),
					],
					subMenuMinWidth: 160,
					subMenuDirection: "inline",
					testId: `chat-subagent-${profile}-open`,
				};
			}),
			subMenuMinWidth: 210,
			testId: "chat-subagent-selector-open",
		},
	];

	return (
		<>
			<ChatModelMenuButton
				models={models}
				value={selectedModel}
				onChange={selectModel}
				disabled={disabled}
				tooltip={loadError ? "モデル一覧を取得できませんでした。" : undefined}
				additionalItems={settingsItems}
			/>
			{supportedEfforts.length > 0 && (
				<ChatEffortSelector
					supportedEfforts={supportedEfforts}
					value={displayedEffort}
					onChange={setSelectedEffort}
					disabled={disabled}
				/>
			)}
		</>
	);
});
